import { Mail, Plus, SearchX } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Skeleton, SkeletonRegion } from '@/components/ui/skeleton';
import { problemMessage } from '../api/problem';
import type { useAccessActions } from '../people/person-actions';
import { StateMessage } from '../ui/StateMessage';
import { InvitationItem } from './InvitationItem';
import type { Invitation, useInvitations } from './use-invitations';

type ListState = ReturnType<typeof useInvitations>['state'];
type Actions = ReturnType<typeof useAccessActions>;

/** The pane's empty states: nothing found under the filters, or nothing sent yet. */
function EmptyState({
  filtered,
  canInvite,
  placeName,
  onClear,
  onInvite,
}: {
  readonly filtered: boolean;
  readonly canInvite: boolean;
  readonly placeName: string;
  readonly onClear: () => void;
  readonly onInvite: () => void;
}) {
  const { t } = useTranslation('administration');
  if (filtered) {
    return (
      <StateMessage
        icon={SearchX}
        title={t('invitations.noResults.title')}
        action={
          <Button variant="outline" onClick={onClear}>
            {t('invitations.noResults.clear')}
          </Button>
        }
      />
    );
  }
  return (
    <StateMessage
      icon={Mail}
      title={t('invitations.firstUse.title', { place: placeName })}
      text={t('invitations.firstUse.text')}
      {...(canInvite
        ? {
            action: (
              <Button onClick={onInvite}>
                <Plus />
                {t('people.invite')}
              </Button>
            ),
          }
        : {})}
    />
  );
}

function ListFoot({
  state,
  more,
  reload,
}: {
  readonly state: ListState;
  readonly more: () => void;
  readonly reload: () => void;
}) {
  const { t } = useTranslation('administration');
  return (
    <div className="people-list-foot">
      <span>
        {state.status === 'ready' ? t('invitations.count', { count: state.total }) : null}
        {' · '}
        {t('invitations.validity')}
      </span>
      {state.status === 'ready' && state.hasMore ? (
        <Button variant="outline" size="sm" onClick={more} disabled={state.loadingMore}>
          {state.loadingMore ? t('common.loading') : t('common.more')}
        </Button>
      ) : null}
      {state.status === 'error' ? (
        <span role="alert" className="flex items-center gap-2">
          {problemMessage(t, state.code, state.httpStatus)}
          <Button variant="outline" size="sm" onClick={reload}>
            {t('common.retry')}
          </Button>
        </span>
      ) : null}
    </div>
  );
}

function ListSkeleton() {
  const { t } = useTranslation('administration');
  return (
    <SkeletonRegion label={t('common.loading')} className="flex flex-col gap-2">
      {[0, 1, 2, 3].map((n) => (
        <Skeleton key={n} className="h-16 w-full" />
      ))}
    </SkeletonRegion>
  );
}

function LoadError({
  state,
  reload,
}: {
  readonly state: Extract<ListState, { status: 'error' }>;
  readonly reload: () => void;
}) {
  const { t } = useTranslation('administration');
  return (
    <StateMessage
      role="alert"
      tone="error"
      icon={Mail}
      title={t('invitations.loadError')}
      text={problemMessage(t, state.code, state.httpStatus)}
      action={
        <Button variant="outline" onClick={reload}>
          {t('common.retry')}
        </Button>
      }
    />
  );
}

/** The list of invitations, or the loading, error or empty state that stands in for it. */
export function InvitationListPane({
  state,
  more,
  reload,
  filtered,
  canInvite,
  placeName,
  actions,
  onClear,
  onInvite,
  onEditRoles,
  peopleHrefFor,
  cardHrefFor,
}: {
  readonly state: ListState;
  readonly more: () => void;
  readonly reload: () => void;
  readonly filtered: boolean;
  readonly canInvite: boolean;
  readonly placeName: string;
  readonly actions: Actions;
  readonly onClear: () => void;
  readonly onInvite: () => void;
  readonly onEditRoles: (invitation: Invitation) => void;
  readonly peopleHrefFor: (invitation: Invitation) => string;
  readonly cardHrefFor: (invitation: Invitation) => string;
}) {
  const { t } = useTranslation('administration');
  if (state.status === 'loading' && state.items.length === 0) return <ListSkeleton />;
  if (state.status === 'error' && state.items.length === 0) {
    return <LoadError state={state} reload={reload} />;
  }
  if (state.items.length === 0) {
    return (
      <EmptyState
        filtered={filtered}
        canInvite={canInvite}
        placeName={placeName}
        onClear={onClear}
        onInvite={onInvite}
      />
    );
  }
  return (
    <>
      <div
        role="list"
        aria-label={t('invitations.listLabel', { place: placeName })}
        className="people-list invitations-list"
      >
        <div className="people-list-head" aria-hidden="true">
          <span>{t('invitations.columns.invitation')}</span>
          <span>{t('invitations.columns.roles')}</span>
          <span>{t('invitations.columns.status')}</span>
          <span>{t('invitations.columns.sent')}</span>
          <span />
        </div>
        {state.items.map((invitation) => (
          <InvitationItem
            key={invitation.id}
            invitation={invitation}
            actions={actions}
            onEditRoles={onEditRoles}
            peopleHref={peopleHrefFor(invitation)}
            cardHref={cardHrefFor(invitation)}
          />
        ))}
      </div>
      <ListFoot state={state} more={more} reload={reload} />
    </>
  );
}
